/**
 * O vocabulário da tela de Relatórios — **texto e classificação, nunca conta.**
 *
 * O grau de confiança chega pronto do servidor como um dos quatro valores declarados em
 * `apps/api/src/modules/report/report-model.ts`. Aqui ele vira rótulo, tom e a frase que
 * explica o que ele quer dizer — e nada mais. Se a tela derivasse o grau (por exemplo,
 * "abaixo de 70% de cobertura é baixa confiança"), existiriam duas definições do mesmo
 * rótulo, e a do arquivo baixado discordaria da que está na tela.
 *
 * O mapeamento para `AttributionConfidence` do `ConfidenceBadge` é o ponto em que o
 * vocabulário do relatório encosta no vocabulário visual do produto: `determinístico` e
 * `provável` são os mesmos três estados do enum `attribution_method` do banco.
 */
import type { AttributionConfidence, BadgeTone } from '@mgdlead/ui';
import type { ReportConfidence } from '../../lib/api';

export interface ConfiancaSpec {
  label: string;
  tone: BadgeTone;
  /** Uma frase: o que este grau quer dizer para quem vai usar o número. */
  explica: string;
  /**
   * O equivalente no `ConfidenceBadge` do design system. `null` para `MEDIDO`: um fato
   * contado **não passa por atribuição nenhuma**, e marcá-lo com o selo de atribuição
   * ensinaria quem lê a desconfiar de um número que não tem por que ser desconfiado.
   */
  badge: AttributionConfidence | null;
}

export const CONFIANCA: Record<ReportConfidence, ConfiancaSpec> = {
  MEDIDO: {
    label: 'Medido',
    tone: 'neutral',
    explica:
      'Fato contado, sem atribuição no meio: veio de um evento que o monitorador registrou ou de um clique que chegou até nós.',
    badge: null,
  },
  DETERMINISTICO: {
    label: 'Determinístico',
    tone: 'success',
    explica:
      'Entrada casada com um clique único dentro da janela de atribuição, sem candidato concorrente.',
    badge: 'deterministic',
  },
  PROVAVEL: {
    label: 'Provável',
    tone: 'info',
    explica:
      'Casamento probabilístico entre clique e entrada. O salto para o WhatsApp não devolve identificador nenhum, então este número é uma estimativa POR BAIXO — e nada é extrapolado sobre a fatia não atribuída.',
    badge: 'probable',
  },
  NAO_ATRIBUIDO: {
    label: 'Não atribuído',
    tone: 'neutral',
    explica:
      'Nenhum casamento entrou neste número. Ele continua somando no total geral, mas não vira desempenho de campanha.',
    badge: 'unattributed',
  },
};

/** As seções do relatório, na ordem em que o servidor as devolve. */
export const SECAO_RESUMIDA: Readonly<Record<string, string>> = {
  'Resumo do período': 'Entradas, saídas, remoções e saldo do recorte inteiro.',
  Atribuição: 'Cliques, casamentos e cobertura — com o grau de confiança de cada número.',
  Permanência: 'Quanto tempo as pessoas ficam, e de quantas delas sabemos isso.',
  'Série diária': 'Um ponto por dia do período.',
  Campanhas: 'O que cada campanha trouxe de clique e de entrada casada.',
  Grupos: 'Entradas, saídas e evasão de cada grupo monitorado.',
};

/** O travessão do vazio. Nunca `0`, nunca célula muda. */
export const TRACO = '—';

/**
 * O valor formatado para a tela.
 *
 * Formata, não calcula: o número já veio pronto, e a unidade já veio declarada pelo
 * servidor. `null` vira travessão — e quem chama é obrigado a mostrar a frase do vazio ao
 * lado, senão o travessão vira defeito aos olhos de quem lê.
 */
export function valorNaTela(value: number | null, unit: string, locale = 'pt-BR'): string {
  if (value === null) return TRACO;
  if (unit === 'percentual') {
    return `${value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  }
  if (unit === 'dias') {
    const n = value.toLocaleString(locale, { maximumFractionDigits: 1 });
    return `${n} ${value === 1 ? 'dia' : 'dias'}`;
  }
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
}

/** `2026-03-10` → `10/03/2026`. O dia do negócio já vem no fuso do workspace. */
export function diaLongo(businessDate: string): string {
  const [ano = '', mes = '', dia = ''] = businessDate.split('-');
  return `${dia}/${mes}/${ano}`;
}
